import { formatDetailValue } from '@/features/contact-group/custom-fields/AdditionalDetailsCell';

/**
 * A contact's custom field values (template or added on the contact) as label / value rows
 * for the CTI caller views.
 * Email is left out: the profile card and Call details tab already show it in its own cell.
 */

const normalize = (name: string) => name.trim().toLowerCase().replace(/[\s_-]+/g, '');

// Same "one edit or less" match the views use to find the email key.
function closeTo(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  const [s, l] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let diff = false;
  while (i < s.length && j < l.length) {
    if (s[i] !== l[j]) {
      if (diff) return false;
      diff = true;
      if (s.length === l.length) i++;
      j++;
    } else {
      i++;
      j++;
    }
  }
  return true;
}

const EMAIL_KEYS = ['email', 'emailaddress', 'emailid'];
const isEmailKey = (key: string) => EMAIL_KEYS.some((c) => closeTo(normalize(key), c));

export interface ExtraDetail {
  id: string;
  label: string;
  value: string;
}

/**
 * Only the contact's real custom fields (`customFieldKeys`: its template's fields and the fields
 * added on it, sent by the backend as `custom_field_keys`); other keys in additional_details can be
 * disposition answers and are not shown.
 */
export function contactExtraDetails(details: unknown, customFieldKeys: unknown): ExtraDetail[] {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return [];
  if (!Array.isArray(customFieldKeys) || !customFieldKeys.length) return [];
  const allowed = new Set(customFieldKeys.map((k) => String(k).trim().toLowerCase()));
  return Object.entries(details as Record<string, unknown>)
    .filter(([key, value]) => {
      if (!allowed.has(key.trim().toLowerCase())) return false;
      if (isEmailKey(key)) return false;
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && !value.trim()) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
    .map(([key, value]) => ({ id: `extra:${key}`, label: key, value: formatDetailValue(value) }));
}
